import { reactive } from 'vue'
import {
  resolveRolling,
  type InstantRangeValue,
  type RollingSpan,
} from '@hantera/design-system'
import type traces from '@/backend/traces'
import type {
  JobSpanDetail,
  RequestDetail,
  SendingSpanDetail,
  TraceDetail,
  TraceSummary,
} from '@/backend/traces.types'
import {
  isTraceId,
  type DateRange,
  type TracesFilters,
  type TracesViewContext,
} from './traces-view-context'

export interface TracesServices {
  tracesService: ReturnType<typeof traces>
}

/**
 * One page. Small enough that the first screen arrives quickly, since each page rescans the window
 * server-side; `DataTable` asks for the next as the operator scrolls.
 */
const PAGE_SIZE = 50

/**
 * Most operators arrive here pre-filtered from an order or a job, where an hour-wide window would
 * hide the very trace they came to find.
 */
const DEFAULT_ROLLING: RollingSpan = { amount: 24, unit: 'hours' }

/**
 * How far back of already-polled time a head fetch re-reads.
 *
 * The API filters *spans* by timestamp and only then groups them into traces, so a summary is
 * computed from the spans that fell inside the requested window. A trace that began ten minutes ago
 * and emits one span right now would come back from a narrow window as a one-span, zero-elapsed
 * trace. Overlapping wide enough that any short trace is wholly contained is what keeps the merged
 * summaries honest; {@link dominates} handles what overlap alone cannot.
 */
const POLL_OVERLAP_MS = 5 * 60_000

/**
 * How many rows a live list keeps. An operator watching for an hour would otherwise accumulate rows
 * without limit; the tail is the oldest, and is what `loadMore()` would fetch again anyway.
 */
const MAX_RETAINED = 1000

/**
 * Whether an incoming summary saw at least as much of its trace as the one already held.
 *
 * A narrower window can only ever truncate a trace, never extend it, so replacing unconditionally
 * would let a head fetch overwrite a fully-loaded trace with a one-span stub. Guarding on this is
 * also what lets a trace that has since failed replace an `ok` row, which is the whole point of
 * leaving auto-refresh on.
 */
function dominates(incoming: TraceSummary, held: TraceSummary): boolean {
  return incoming.startedAt <= held.startedAt && incoming.spanCount >= held.spanCount
}

/** Newest first, matching the server's `(startedAt, traceId)` ordering. */
function byRecency(a: TraceSummary, b: TraceSummary): number {
  const started = b.startedAt.getTime() - a.startedAt.getTime()
  return started !== 0 ? started : b.traceId.localeCompare(a.traceId)
}


export class TracesViewContextImpl implements TracesViewContext {
  readonly items = reactive<TraceSummary[]>([])

  readonly filters = reactive<TracesFilters>({
    resources: [],
    traceId: null,
    status: null,
  })

  private readonly dateRangeState = reactive<{ value: DateRange | null }>({
    value: resolveRolling(DEFAULT_ROLLING),
  })

  private readonly rollingSpan = reactive<{ value: RollingSpan | null }>({
    value: { ...DEFAULT_ROLLING },
  })

  /** The `to` of the last successful fetch, and where the next head fetch starts from. */
  private polledThrough = new Date()

  readonly listState = reactive({
    isLoading: false,
    error: null as string | null,
  })

  // Boxed so the getter below can hand out a plain `TraceDetail | null` per the interface, while the
  // reference itself stays reactive when a new trace replaces the old one.
  private readonly openTrace = reactive<{ value: TraceDetail | null }>({ value: null })

  // The trace `loadTrace()` was last asked for, which is known before the fetch resolves. `openTrace`
  // cannot serve here: it is null for the whole of the load a deep-linked span needs to survive.
  private readonly requestedTraceId = reactive<{ value: string | null }>({ value: null })

  // Tagged with the trace it addresses, so `loadTrace()` need not clear it: clearing on load would
  // discard a span selected before its trace arrived, which is exactly how a deep link arrives.
  private readonly selectedSpan = reactive<{ traceId: string | null; value: string | undefined }>({
    traceId: null,
    value: undefined,
  })

  readonly traceState = reactive({
    isLoading: false,
    error: null as string | null,
  })

  /** A null entry is meaningful: asked, and there is nothing. */
  private readonly requestDetails = reactive(new Map<string, RequestDetail | null>())
  private readonly loadingRequests = reactive(new Set<string>())

  /**
   * Job and sending payloads, cached the same way and for the same reason.
   *
   * Separate maps rather than one keyed by kind: the ids are GUIDs from different tables, and a shared
   * map would let a job id shadow a sending id on the astronomically unlikely collision while making
   * every read need a cast.
   */
  private readonly jobDetails = reactive(new Map<string, JobSpanDetail | null>())
  private readonly sendingDetails = reactive(new Map<string, SendingSpanDetail | null>())
  private readonly loadingSpans = reactive(new Set<string>())

  /**
   * Where the next page resumes. Null means either "not yet paged" or "no more pages" —
   * `hasMore` is what distinguishes those.
   */
  private cursor: string | null = null

  /**
   * Guards against re-entrant fetches.
   *
   * Set synchronously before the first await, because `DataTable` calls `loadMore()` from a
   * scroll/resize handler that can fire while the mount's `refresh()` is still in flight —
   * `listState.isLoading` is set inside the async body and lands too late to stop it.
   */
  private inFlight = false

  private readonly tracesService: ReturnType<typeof traces>

  constructor(services: TracesServices) {
    this.tracesService = services.tracesService
  }

  get dateRange(): DateRange | null {
    return this.dateRangeState.value
  }

  get rolling(): RollingSpan | null {
    return this.rollingSpan.value
  }

  get trace(): TraceDetail | null {
    return this.openTrace.value
  }

  get openSpan(): string | undefined {
    return this.selectedSpan.traceId === this.requestedTraceId.value
      ? this.selectedSpan.value
      : undefined
  }

  /**
   * Reported by the server, never inferred from the row count: a page that happens to be exactly
   * `PAGE_SIZE` long may still be the last, and guessing otherwise makes `DataTable` poll
   * `loadMore()` forever.
   */
  get hasMore(): boolean {
    return this.more
  }

  private more = false

  async refresh(): Promise<void> {
    this.cursor = null
    await this.fetchPage(true)
  }

  async loadMore(): Promise<void> {
    if (!this.more || this.cursor == null) return

    await this.fetchPage(false)
  }

  /**
   * The in-flight guard is set synchronously before the first await, because `DataTable` calls
   * `loadMore()` from a scroll/resize handler that can fire while the mount's `refresh()` is still
   * running — `listState.isLoading` is assigned inside the async body and lands too late to stop it.
   */
  private async fetchPage(replace: boolean): Promise<void> {
    if (this.inFlight) return

    this.inFlight = true
    this.listState.isLoading = true
    this.listState.error = null

    try {
      const page = await this.tracesService.list({
        from: this.dateRange?.from,
        to: this.dateRange?.to,
        status: this.filters.status ?? undefined,
        traceId: this.filters.traceId ?? undefined,
        resource: [...this.filters.resources],
        cursor: replace ? undefined : (this.cursor ?? undefined),
        limit: PAGE_SIZE,
      })

      if (replace) {
        this.items.splice(0, this.items.length, ...page.items)
      } else {
        this.append(page.items)
      }

      this.cursor = page.nextCursor ?? null
      this.more = page.hasMore && this.cursor != null
      this.polledThrough = this.dateRange?.to ?? new Date()
    } catch (e: any) {
      console.error('Failed to load traces:', e)
      this.listState.error = e?.message ?? 'Failed to load traces'

      // Stop rather than retry: `DataTable` would otherwise ask again on the next scroll and keep
      // hitting the same failure.
      this.more = false
    } finally {
      this.listState.isLoading = false
      this.inFlight = false
    }
  }

  /**
   * Skips ids already held. Live traffic can extend a trace between two page fetches, moving its
   * `startedAt` and pushing it back across the boundary the previous cursor drew.
   */
  private append(page: TraceSummary[]): void {
    const seen = new Set(this.items.map(t => t.traceId))

    for (const summary of page) {
      if (!seen.has(summary.traceId)) {
        this.items.push(summary)
      }
    }
  }



  /**
   * Slides a rolling window forward and merges what is new into the head of the list.
   *
   * Deliberately not a `refresh()`: that drops the cursor and replaces `items`, so an operator
   * scrolled several pages deep would be thrown back to the first page on every interval, losing
   * their position and their selection. Merging leaves the loaded pages, the cursor and `hasMore`
   * exactly as they were, so paging resumes from where it left off.
   */
  async pollNewer(): Promise<void> {
    const span = this.rollingSpan.value
    if (span == null || this.inFlight) return

    const { from, to } = resolveRolling(span)
    this.dateRangeState.value = { from, to }

    const overlapFrom = new Date(this.polledThrough.getTime() - POLL_OVERLAP_MS)
    const head = overlapFrom > from ? overlapFrom : from

    this.inFlight = true

    try {
      const page = await this.tracesService.list({
        from: head,
        to,
        status: this.filters.status ?? undefined,
        traceId: this.filters.traceId ?? undefined,
        resource: [...this.filters.resources],
        limit: PAGE_SIZE,
      })

      this.mergeNewer(page.items, page.hasMore)
      this.polledThrough = to
      this.listState.error = null
    } catch (e: any) {
      console.error('Failed to poll traces:', e)
      // Left silent on the list: the rows already shown are still valid, and a poll that fails once
      // will be retried on the next interval anyway.
    } finally {
      this.inFlight = false
    }
  }

  /**
   * Merges a head page into the list, or falls back to a full refresh when it cannot be merged.
   *
   * A full page sharing no trace with the list means more than a page arrived within one interval,
   * so there is a gap between the page and the rows held and merging would leave the list with a
   * hole in the middle of it. Replacing is correct precisely then — the list is churning faster than
   * anyone can read it, so nothing is lost by rebuilding.
   */
  private mergeNewer(page: TraceSummary[], hasMorePage: boolean): void {
    if (page.length === 0) return

    const held = new Map(this.items.map((t, index) => [t.traceId, index]))
    const isDisjoint = hasMorePage && page.every(t => !held.has(t.traceId))

    if (isDisjoint) {
      this.cursor = null
      void this.fetchPage(true)
      return
    }

    for (const summary of page) {
      const index = held.get(summary.traceId)

      if (index == null) {
        this.items.push(summary)
      } else if (dominates(summary, this.items[index])) {
        this.items[index] = summary
      }
    }

    // Sorted rather than unshifted: a long-scheduled job running now gives its trace a `startedAt`
    // older than rows already held, so a new trace does not always belong at the top.
    this.items.sort(byRecency)

    if (this.items.length > MAX_RETAINED) {
      this.items.splice(MAX_RETAINED)
    }
  }

  /**
   * Rows already loaded are kept even when a sliding window moves past them: the window governs what
   * is fetched, not what is retained, and pulling a row out from under someone reading it is worse
   * than showing one slightly older than the range says.
   */
  setRange(range: InstantRangeValue | null): void {
    if (range == null) {
      this.rollingSpan.value = null
      this.dateRangeState.value = null
      return
    }

    this.rollingSpan.value = range.rolling ? { ...range.rolling } : null

    // A range reaching into the future has no data in it, and would pad the axis with empty buckets.
    const now = new Date()
    let next = range.to > now ? { from: range.from, to: now } : { from: range.from, to: range.to }
    if (next.from > next.to) {
      next = { from: next.to, to: next.from }
    }

    this.dateRangeState.value = next
  }

  addTerm(term: string): void {
    const trimmed = term.trim()
    if (!trimmed) return

    if (isTraceId(trimmed)) {
      this.filters.traceId = trimmed
      return
    }

    if (!this.filters.resources.includes(trimmed)) {
      this.filters.resources.push(trimmed)
    }
  }

  removeTerm(term: string): void {
    const index = this.filters.resources.indexOf(term)
    if (index >= 0) {
      this.filters.resources.splice(index, 1)
    }
  }

  clearTraceId(): void {
    this.filters.traceId = null
  }

  async loadTrace(traceId: string): Promise<void> {
    this.traceState.isLoading = true
    this.traceState.error = null
    this.openTrace.value = null
    this.requestedTraceId.value = traceId
    this.requestDetails.clear()
    this.jobDetails.clear()
    this.sendingDetails.clear()

    try {
      this.openTrace.value = await this.tracesService.get(traceId)
    } catch (e: any) {
      console.error('Failed to load trace:', e)
      this.traceState.error = e?.message ?? 'Failed to load trace'
    } finally {
      this.traceState.isLoading = false
    }
  }

  selectSpan(traceId: string, span: string | undefined): void {
    this.selectedSpan.traceId = traceId
    this.selectedSpan.value = span
  }


  /**
   * Caching absence as well as presence is what stops a re-selected span from re-requesting a 404
   * every time the operator clicks back to it.
   */
  async loadRequest(traceId: string, requestId: string): Promise<void> {
    if (this.requestDetails.has(requestId) || this.loadingRequests.has(requestId)) {
      return
    }

    this.loadingRequests.add(requestId)

    try {
      const detail = await this.tracesService.getRequest(traceId, requestId)
      this.requestDetails.set(requestId, detail ?? null)
    } catch (e) {
      console.error('Failed to load request detail:', e)
      this.requestDetails.set(requestId, null)
    } finally {
      this.loadingRequests.delete(requestId)
    }
  }

  /**
   * Appends frames that arrived after the cached detail was fetched.
   *
   * Only the tail is requested — `frameOffset` is however many are already held — so following a busy
   * stream costs one page per poll rather than re-reading every frame from the start.
   */
  async loadMoreFrames(traceId: string, requestId: string): Promise<void> {
    const held = this.requestDetails.get(requestId)

    // Nothing cached yet, or cached as absent: loadRequest owns both of those cases.
    if (!held || this.loadingRequests.has(requestId)) {
      return
    }

    const frames = held.responseFrames
    if (!frames || held.frameCount == null || held.frameCount <= frames.length) {
      return
    }

    this.loadingRequests.add(requestId)

    try {
      const next = await this.tracesService.getRequest(traceId, requestId, frames.length)

      if (next?.responseFrames?.length) {
        this.requestDetails.set(requestId, {
          ...next,
          responseFrames: [...frames, ...next.responseFrames],
        })
      }
    } catch (e) {
      // A failed tail fetch leaves the frames already held in place: a transient error must not
      // discard payload the operator is reading.
      console.error('Failed to load further response frames:', e)
    } finally {
      this.loadingRequests.delete(requestId)
    }
  }

  requestDetail(requestId: string): RequestDetail | null | undefined {
    return this.requestDetails.get(requestId)
  }

  isLoadingRequest(requestId: string): boolean {
    return this.loadingRequests.has(requestId)
  }

  /** @see loadRequest — same caching contract, including caching absence. */
  async loadJobSpan(traceId: string, jobId: string): Promise<void> {
    if (this.jobDetails.has(jobId) || this.loadingSpans.has(jobId)) {
      return
    }

    this.loadingSpans.add(jobId)

    try {
      const detail = await this.tracesService.getJob(traceId, jobId)
      this.jobDetails.set(jobId, detail ?? null)
    } catch (e) {
      console.error('Failed to load job span detail:', e)
      this.jobDetails.set(jobId, null)
    } finally {
      this.loadingSpans.delete(jobId)
    }
  }

  /** @see loadRequest — same caching contract, including caching absence. */
  async loadSendingSpan(traceId: string, sendingId: string): Promise<void> {
    if (this.sendingDetails.has(sendingId) || this.loadingSpans.has(sendingId)) {
      return
    }

    this.loadingSpans.add(sendingId)

    try {
      const detail = await this.tracesService.getSending(traceId, sendingId)
      this.sendingDetails.set(sendingId, detail ?? null)
    } catch (e) {
      console.error('Failed to load sending span detail:', e)
      this.sendingDetails.set(sendingId, null)
    } finally {
      this.loadingSpans.delete(sendingId)
    }
  }

  jobSpanDetail(jobId: string): JobSpanDetail | null | undefined {
    return this.jobDetails.get(jobId)
  }

  sendingSpanDetail(sendingId: string): SendingSpanDetail | null | undefined {
    return this.sendingDetails.get(sendingId)
  }

  isLoadingSpan(spanId: string): boolean {
    return this.loadingSpans.has(spanId)
  }
}
